import { useCallback, useMemo, useRef, useState } from "react";
import { ArrowRight, Play } from "lucide-react";

import { useVimeoPlayer } from "@/hooks/useVimeoPlayer";
import { buildVimeoEmbedUrl, extractVimeoId, formatTime } from "@/lib/helpers";
import type { VideoIndexRow, VideoRow } from "@/lib/schema";

const defaultInstructorImage =
  "https://cdn.prod.website-files.com/686229256a807206c1874545/687bcf08468fe4993282a9ec_Khusbu.avif";

interface VideoPlayerLayoutProps {
  courseVideos: VideoRow[];
  videoIndex: VideoIndexRow[] | null;
  instructorName?: string;
  actionButtons?: React.ReactNode;
}

export function VideoPlayerLayoutSkeleton() {
  return (
    <div className="flex flex-1 flex-col px-[clamp(1rem,3vw,3rem)] pt-6 pb-10 min-[768px]:flex-row min-[768px]:gap-5">
      <div className="min-w-0 flex-1">
        <div className="aspect-video w-full animate-pulse rounded-xl bg-[#e2dce0]" />
        <div className="flex items-center gap-[0.65rem] pt-5 pb-2">
          <div className="h-8 w-8 animate-pulse rounded-[0.3rem] bg-[#e2dce0]" />
          <div className="flex flex-col gap-1.5">
            <div className="h-2 w-14 animate-pulse rounded bg-[#e2dce0]" />
            <div className="h-3 w-24 animate-pulse rounded bg-[#e2dce0]" />
          </div>
        </div>
      </div>
      <aside className="w-full shrink-0 min-[768px]:w-[18rem] min-[1400px]:w-[20rem]">
        <div className="rounded-xl bg-white">
          <div className="flex items-center gap-[0.6rem] px-5 py-5">
            <div className="h-[1.15rem] w-[1.15rem] animate-pulse rounded bg-[#e2dce0]" />
            <div className="h-4 w-16 animate-pulse rounded bg-[#e2dce0]" />
          </div>
          <div className="flex flex-col gap-3 px-3.5 pb-3.5">
            {[1, 2].map((i) => (
              <div key={i} className="rounded-lg bg-[#ede4e9] px-5 py-5">
                <div className="mb-3 h-4 w-16 animate-pulse rounded bg-[#d6ccd2]" />
                <div className="mb-5 flex items-center gap-3">
                  <div className="h-8 w-8 animate-pulse rounded-full bg-[#c9b4c1]" />
                  <div className="h-3.5 w-32 animate-pulse rounded bg-[#d6ccd2]" />
                </div>
                <div className="flex flex-col gap-4">
                  <div className="h-3 w-40 animate-pulse rounded bg-[#d6ccd2]" />
                  <div className="h-3 w-36 animate-pulse rounded bg-[#d6ccd2]" />
                  <div className="h-3 w-44 animate-pulse rounded bg-[#d6ccd2]" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </aside>
    </div>
  );
}

export function VideoPlayerLayout({
  courseVideos,
  videoIndex,
  instructorName = "Khusbu Gemmati",
  actionButtons,
}: VideoPlayerLayoutProps) {
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);

  const currentVideo = useMemo(() => {
    if (activeVideoId) {
      return courseVideos.find((v) => v.id === activeVideoId) ?? courseVideos[0] ?? null;
    }
    return courseVideos[0] ?? null;
  }, [courseVideos, activeVideoId]);

  // Build direct iframe src for instant load
  const iframeSrc = useMemo(() => {
    if (!currentVideo?.video_url) return "";
    return buildVimeoEmbedUrl(currentVideo.video_url, { autoplay: true });
  }, [currentVideo?.video_url]);

  // iframe ref — SDK attaches to this for seeking control
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const { seekTo, loadVideo, isReady, attachToIframe } = useVimeoPlayer(iframeRef);

  // Attach SDK when iframe mounts via ref callback
  const iframeRefCallback = useCallback(
    (el: HTMLIFrameElement | null) => {
      (iframeRef as React.MutableRefObject<HTMLIFrameElement | null>).current = el;
      if (el) attachToIframe();
    },
    [attachToIframe],
  );

  // Build a map of video_id -> sorted timestamps
  const timestampsByVideo = useMemo(() => {
    const map: Record<string, VideoIndexRow[]> = {};
    if (!videoIndex) return map;
    for (const entry of videoIndex) {
      const vid = entry.video_id;
      if (!vid) continue;
      if (!map[vid]) map[vid] = [];
      map[vid].push(entry);
    }
    for (const key of Object.keys(map)) {
      map[key]!.sort((a, b) => (a.start_second ?? 0) - (b.start_second ?? 0));
    }
    return map;
  }, [videoIndex]);

  const handleVideoClick = useCallback(
    (videoId: string) => {
      setActiveVideoId(videoId);
      const video = courseVideos.find((v) => v.id === videoId);
      if (!video?.video_url) return;
      const vimeoId = extractVimeoId(video.video_url);
      if (vimeoId) loadVideo(vimeoId);
    },
    [courseVideos, loadVideo],
  );

  const handleTimestampClick = useCallback(
    (videoId: string, startSecond: number) => {
      if (currentVideo?.id !== videoId) {
        setActiveVideoId(videoId);
        const video = courseVideos.find((v) => v.id === videoId);
        if (!video?.video_url) return;
        const vimeoId = extractVimeoId(video.video_url);
        if (vimeoId) loadVideo(vimeoId, startSecond);
      } else {
        seekTo(startSecond);
      }
    },
    [currentVideo?.id, courseVideos, loadVideo, seekTo],
  );

  return (
    <div className="flex flex-1 flex-col px-[clamp(1rem,3vw,3rem)] pt-6 pb-10 min-[768px]:flex-row min-[768px]:gap-5">
      {/* Left: video + instructor bar */}
      <div className="min-w-0 flex-1">
        {/* Video player — direct iframe for fast load, SDK attaches for control */}
        <div className="relative aspect-video w-full overflow-hidden rounded-xl">
          {iframeSrc ? (
            <iframe
              ref={iframeRefCallback}
              src={iframeSrc}
              className="absolute inset-[-2px] h-[calc(100%+4px)] w-[calc(100%+4px)] border-0 outline-none"
              style={{ border: 0 }}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              loading="eager"
              fetchPriority="high"
              title={currentVideo?.title ?? "Video"}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-black">
              <Play className="h-16 w-16 text-white/60" />
            </div>
          )}
        </div>

        {/* Instructor + action buttons */}
        <div className="flex flex-wrap items-center justify-between gap-4 pt-5 pb-2">
          <div className="flex items-center gap-[0.65rem]">
            <img
              src={defaultInstructorImage}
              alt={instructorName}
              className="h-8 w-8 rounded-[0.3rem] object-cover"
            />
            <div>
              <div className="text-[0.58rem] font-bold uppercase tracking-[0.25em] text-[#b0a8ad]">
                Instructor
              </div>
              <div className="text-[0.82rem] font-bold leading-[1.15] text-[#3a3338]">
                {instructorName}
              </div>
            </div>
          </div>
          {actionButtons && (
            <div className="ml-auto flex items-center gap-3">{actionButtons}</div>
          )}
        </div>
      </div>

      {/* Right sidebar — Tutorial card */}
      <aside className="w-full shrink-0 min-[768px]:w-[18rem] min-[1400px]:w-[20rem]">
        <div className="rounded-xl bg-white">
          {/* Tutorial header */}
          <div className="flex items-center gap-[0.6rem] px-5 py-5">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-[1.15rem] w-[1.15rem] text-[#4a3f46]"
              aria-hidden="true"
            >
              <rect x="2" y="3" width="20" height="14" rx="2" />
              <line x1="8" y1="21" x2="16" y2="21" />
              <line x1="12" y1="17" x2="12" y2="21" />
              <polygon points="10,7.5 10,12.5 14.5,10" fill="currentColor" stroke="none" />
            </svg>
            <span className="text-[0.95rem] font-medium text-[#2d2329]">Tutorial</span>
          </div>

          {/* Video cards */}
          <div className="flex max-h-[calc(100vh-14rem)] flex-col gap-3 overflow-y-auto px-3.5 pb-3.5">
            {courseVideos.map((video, idx) => {
              const videoNumber = idx + 1;
              const videoTs = timestampsByVideo[video.id] ?? [];

              const isActive = currentVideo?.id === video.id;

              return (
                <div key={video.id} className={`rounded-lg px-5 py-5 ${isActive ? "bg-[#e2d5de] ring-2 ring-[#762a5c]/30" : "bg-[#ede4e9]"}`}>
                  <p className="mb-3 text-base font-bold text-[#4a3f46]">
                    Video: {videoNumber}
                  </p>

                  <button
                    type="button"
                    className="mb-5 flex w-full items-center gap-3 text-left text-[#2d2329] transition-colors hover:text-pink-darker"
                    onClick={() => handleVideoClick(video.id)}
                  >
                    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${isActive ? "bg-[#762a5c]" : "bg-[#c9b4c1]"}`}>
                      <Play className={`h-3.5 w-3.5 ${isActive ? "fill-white text-white" : "fill-[#5c2d4f] text-[#5c2d4f]"}`} />
                    </span>
                    <span className="text-[0.95rem] font-medium">{video.title}</span>
                  </button>

                  {videoTs.length > 0 && (
                    <div className="flex flex-col gap-5">
                      {videoTs.map((ts) => (
                        <button
                          key={ts.id}
                          type="button"
                          className="flex items-center gap-3 text-left text-[0.95rem] text-[#3a2e36] underline decoration-[#3a2e36]/40 underline-offset-2 hover:text-pink-darker hover:decoration-pink-darker"
                          onClick={() => handleTimestampClick(video.id, ts.start_second ?? 0)}
                        >
                          <ArrowRight className="h-5 w-5 shrink-0 text-[#6b5e67]" />
                          <span>
                            {formatTime(ts.start_second ?? 0)} - {ts.name}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </aside>
    </div>
  );
}
